import { CircularProgress } from '@civilgrid/ui/components';
import { type ButtonHTMLAttributes, forwardRef, type ReactNode, useMemo } from 'react';
import Icon, { type IconName } from '~/components2/Icon';

export interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  alternate?: boolean;
  danger?: boolean;
  icon?: IconName;
  iconSize?: number;
  isProcessing?: boolean;
  dark?: boolean;
  ghost?: boolean;
  small?: boolean;
}

const Button = forwardRef<HTMLButtonElement, Props>(
  ({ children, alternate, danger, icon, iconSize, isProcessing, dark, ghost, small, ...props }, ref) => {
    let commonClassNames =
      'cursor-pointer text-sm font-normal rounded-sm border border-solid border-brand bg-brand text-white text-center capitalize focus-ring disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:hover:opacity-100';

    let containerClassNames = '';

    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
    const extraStyles = useMemo(() => {
      let styles = '';
      if (alternate === true) {
        styles += 'bg-white !border-secondary/30 !text-primary hover:bg-gray-100 hover:!opacity-100 ';
      } else if (danger === true) {
        styles += 'bg-red-500 !text-white !border-red-500 hover:bg-red-400 hover:!opacity-100 ';
      } else if (ghost === true) {
        styles += 'bg-transparent !border-none !text-primary hover:bg-gray-100 hover:!opacity-100 ';
      } else if (dark === true) {
        styles += '!text-white !border-black hover:bg-gray-800 hover:!opacity-100 ';
      }

      if (small === true) {
        styles += 'py-0.5 '; // smaller padding for small size
      } else {
        styles += 'p-1.5 '; // original padding for normal size
      }
      return styles.trim();
    }, [danger, alternate, ghost, small]);

    if (children) {
      iconSize = iconSize || 14;
      commonClassNames = `${commonClassNames} min-w-[100px]`;
      containerClassNames = 'flex items-center justify-center space-x-1 px-2';
    } else {
      // just icon
      iconSize = iconSize || 16;
      containerClassNames = 'flex items-center text-base';
    }

    let content: ReactNode;

    if (isProcessing) {
      content = (
        <span className={containerClassNames}>
          {children && <span className="sr-only">{children}</span>}
          <CircularProgress
            indeterminate
            tone={props.disabled ? 'neutral' : alternate || ghost ? 'brand' : 'inverse'}
          />
        </span>
      );
    } else {
      content = (
        <span className={containerClassNames}>
          {icon && <Icon icon={icon} size={iconSize} />}
          <span>{children}</span>
        </span>
      );
    }

    return (
      <button
        ref={ref}
        // ensure forms don't get accidentally submitted.
        // If other types were passed in, they will overriden on the next line
        type="button"
        {...props}
        className={`${commonClassNames} ${extraStyles} hover:opacity-75`}
      >
        {content}
      </button>
    );
  },
);

Button.displayName = 'Button';
export default Button;
